/**
 * Wildlife Report Utility Functions
 */
import { 
  SPECIES_OPTIONS, 
  UTM_ZONE_OPTIONS, 
  ANIMAL_DISTANCE_OPTIONS, 
  ASPECT_OF_ANIMAL_OPTIONS, 
  ELEVATION_OPTIONS, 
  ANIMAL_REACTION_OPTIONS, 
  ACTION_OPTIONS, 
  INVENTORY_METHOD_OPTIONS, 
  MACHINE_TYPE_OPTIONS 
} from '../constants/wildlifeDropdowns';

/**
 * Validates if a wildlife report with the given species and zones is valid
 *
 * Rules:
 * 1. If species is "No Observation", at least one zone must be selected
 * 2. Ski run is optional for all reports
 * 3. If species is not "No Observation" and multiple zones are selected, validation fails
 * 4. All dropdown selections must match the standardized options
 *
 * @param {Object} report - The wildlife report object
 * @param {String} report.species - The selected species
 * @param {Array} report.selectedZones - Array of selected zones
 * @param {String} report.ski_run - The selected ski run (optional)
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateWildlifeReport = (report) => {
  const {
    species,
    selectedZones
  } = report;

  // Check if species is selected
  if (!species) {
    return {
      isValid: false,
      message: 'Species is required'
    };
  }

  // Check if at least one zone is selected
  if (!selectedZones || selectedZones.length === 0) {
    return {
      isValid: false,
      message: 'At least one zone must be selected'
    };
  }

  // If species is "No Observation", no need for specific run
  // "No Observation" can be applied to multiple zones
  if (species === 'No Observation') {
    // Even for "No Observation", validate any provided dropdown values
    // against standardized options (if they are provided)
    const validationResult = validateDropdownSelections(report);
    if (!validationResult.isValid) {
      return validationResult;
    }

    return {
      isValid: true,
      message: ''
    };
  }

  // For actual wildlife observations, only one zone can be selected
  if (selectedZones.length > 1) {
    return {
      isValid: false,
      message: 'For wildlife observations, only one zone can be selected. Multiple zones are only allowed for "No Observation".'
    };
  }

  // Ski run is now optional for all reports

  // Validate all dropdown selections against standardized options
  const validationResult = validateDropdownSelections(report);
  if (!validationResult.isValid) {
    return validationResult;
  }

  return {
    isValid: true,
    message: ''
  };
};

/**
 * Validates all dropdown selections against standardized options
 * 
 * @param {Object} report - The wildlife report object
 * @returns {Object} - Validation result with isValid and message properties
 */
const validateDropdownSelections = (report) => {
  const { 
    species
  } = report;
  
  // Validate species
  if (species && !SPECIES_OPTIONS.includes(species)) {
    return {
      isValid: false,
      message: `"${species}" is not a valid species option`
    };
  }
  
  // Validate UTM Zone
  if (report.utm_zone && !UTM_ZONE_OPTIONS.includes(report.utm_zone)) {
    return {
      isValid: false,
      message: `"${report.utm_zone}" is not a valid UTM Zone option`
    };
  }
  
  // For wildlife observations, validate animal-specific fields
  if (species !== 'No Observation') {
    // Validate Animal Distance
    if (report.animal_distance && !ANIMAL_DISTANCE_OPTIONS.includes(report.animal_distance)) {
      return {
        isValid: false,
        message: `"${report.animal_distance}" is not a valid Animal Distance option`
      };
    }
    
    // Validate Aspect of Animal
    if (report.aspect_of_animal && !ASPECT_OF_ANIMAL_OPTIONS.includes(report.aspect_of_animal)) {
      return {
        isValid: false,
        message: `"${report.aspect_of_animal}" is not a valid Aspect of Animal option`
      };
    }
    
    // Validate Elevation
    if (report.elevation && !ELEVATION_OPTIONS.includes(report.elevation)) {
      return {
        isValid: false,
        message: `"${report.elevation}" is not a valid Elevation option`
      };
    }
    
    // Validate Animal Reaction
    if (report.animal_reaction && !ANIMAL_REACTION_OPTIONS.includes(report.animal_reaction)) {
      return {
        isValid: false,
        message: `"${report.animal_reaction}" is not a valid Animal Reaction option`
      };
    }
    
    // Validate Immediate Action
    if (report.immediate_action && !ACTION_OPTIONS.includes(report.immediate_action)) {
      return {
        isValid: false,
        message: `"${report.immediate_action}" is not a valid Immediate Action option`
      };
    }
    
    // Validate End of Day Action
    if (report.end_of_day_action && !ACTION_OPTIONS.includes(report.end_of_day_action)) {
      return {
        isValid: false,
        message: `"${report.end_of_day_action}" is not a valid End of Day Action option`
      };
    }
  }
  
  // Validate Inventory Method
  if (report.inventory_method && !INVENTORY_METHOD_OPTIONS.includes(report.inventory_method)) {
    return {
      isValid: false,
      message: `"${report.inventory_method}" is not a valid Inventory Method option`
    };
  }
  
  // Validate Type of Machine
  if (report.type_of_machine && !MACHINE_TYPE_OPTIONS.includes(report.type_of_machine)) {
    return {
      isValid: false,
      message: `"${report.type_of_machine}" is not a valid Type of Machine option`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Prepares wildlife report data for submission
 * 
 * @param {Object} report - The wildlife report form data
 * @returns {Object} - Prepared data for API submission
 */
export const prepareWildlifeReportData = (report) => {
  const isNoObservation = report.species === 'No Observation';
  
  // Base report data
  const reportData = {
    ...report,
    // For "No Observation", add a default comment if none provided
    comments: report.comments || (isNoObservation ? 'No wildlife observed in selected zones' : '')
  };
  
  // Convert elevation string to integer
  if (reportData.elevation) {
    // Map elevation strings to integer values
    const elevationMap = {
      // New elevation options
      'Alpine': 3,
      'Treeline': 2,
      'Below Treeline': 1,
      // Legacy elevation options (for backward compatibility)
      'Alpine (above 2000m)': 3,
      'Mid-slope (1500-2000m)': 2,
      'Valley Bottom (below 1500m)': 1,
      'Unknown': null
    };

    reportData.elevation = elevationMap[reportData.elevation] || null;
  }
  
  // Convert selected zones to the format expected by the API
  reportData.zones = report.selectedZones.map(zone => ({
    zone_id: zone.id,
    zone_name: zone.name,
    // For "No Observation", mark all zones as having no observations
    // For wildlife observations, the specific zone has the observation
    has_observation: !isNoObservation,
    // For "No Observation", no specific run is associated with the zone
    // For wildlife observations, associate the run with the zone regardless of which zone it is
    // This allows the ski run field to work independently from zone selection
    run: isNoObservation ? null : report.ski_run
  }));
  
  // Remove the selectedZones property as it's now converted to zones
  delete reportData.selectedZones;
  
  return reportData;
};

/**
 * Checks if the species is "No Observation" or "No Animal Observation"
 *
 * @param {String} species - The selected species
 * @returns {Boolean} - True if the species is "No Observation" or "No Animal Observation"
 */
export const isNoObservation = (species) => {
  return species === 'No Observation' || species === 'No Animal Observation';
};

/**
 * Gets the list of species options
 * 
 * @returns {Array} - List of species options
 */
export const getSpeciesOptions = () => {
  return SPECIES_OPTIONS;
};

/**
 * Converts elevation integer to string representation
 *
 * @param {Number} elevationValue - The elevation integer value
 * @returns {String} - The string representation of the elevation
 */
export const getElevationName = (elevationValue) => {
  const elevationMap = {
    3: 'Alpine',
    2: 'Treeline',
    1: 'Below Treeline'
  };

  return elevationMap[elevationValue] || 'Unknown';
};

export default {
  validateWildlifeReport,
  prepareWildlifeReportData,
  isNoObservation,
  getSpeciesOptions,
  getElevationName
};